import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Label,
  Badge,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { api } from '../../../config';
import { DEFAULT_RIGHTS } from '../constants';
import { buildAssignToastMessage, getMenuName } from '../utils/menuRoleUtils';
import DualListPanel from './DualListPanel';

const AssignMenuModal = ({
  isOpen,
  toggle,
  token,
  selectedRole,
  menuOptions,
  onLoadingChange,
}) => {
  const [modalLoading, setModalLoading] = useState(false);
  const [originalAssignments, setOriginalAssignments] = useState([]);
  const [originalMenuIds, setOriginalMenuIds] = useState([]);
  const [assignedMenuIds, setAssignedMenuIds] = useState([]);
  const [leftSelection, setLeftSelection] = useState([]);
  const [rightSelection, setRightSelection] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [lastLeftIndex, setLastLeftIndex] = useState(null);
  const [lastRightIndex, setLastRightIndex] = useState(null);

  const resetState = () => {
    setAssignedMenuIds([]);
    setOriginalMenuIds([]);
    setOriginalAssignments([]);
    setLeftSelection([]);
    setRightSelection([]);
    setLeftSearch('');
    setRightSearch('');
    setHasChanges(false);
    setLastLeftIndex(null);
    setLastRightIndex(null);
  };

  useEffect(() => {
    if (!isOpen || !selectedRole || !token) return;

    const loadAssignments = async () => {
      resetState();
      onLoadingChange?.(true);

      try {
        const response = await fetch(
          `${api.API_URL}/menu-role/by-role/${selectedRole.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Failed to load assignment data');

        const result = await response.json();
        const rawData = result.data?.items ?? result.data;
        const existingItems = Array.isArray(rawData) ? rawData : [];

        setOriginalAssignments(existingItems);

        const existingMenuIds = existingItems
          .filter(item => item && item.menuId != null)
          .map(item => String(item.menuId));

        setOriginalMenuIds(existingMenuIds);
        setAssignedMenuIds(existingMenuIds);
      } catch (error) {
        toast.error(`Error: ${error.message}`);
        toggle();
      } finally {
        onLoadingChange?.(false);
      }
    };

    loadAssignments();
  }, [isOpen, selectedRole, token]);

  useEffect(() => {
    if (!isOpen) return;

    const currentSet = new Set(assignedMenuIds.map(String));
    const originalSet = new Set(originalMenuIds.map(String));

    const isDifferent =
      assignedMenuIds.length !== originalMenuIds.length ||
      assignedMenuIds.some(id => !originalSet.has(id)) ||
      originalMenuIds.some(id => !currentSet.has(id));

    setHasChanges(isDifferent);
  }, [assignedMenuIds, originalMenuIds, isOpen]);

  useEffect(() => {
    setLastLeftIndex(null);
  }, [leftSearch]);

  useEffect(() => {
    setLastRightIndex(null);
  }, [rightSearch]);

  const handleClose = () => {
    resetState();
    toggle();
  };

  const assignedSet = new Set(assignedMenuIds.map(String));

  const availableMenus = menuOptions.filter(
    m =>
      !assignedSet.has(String(m.id)) &&
      (m.label || m.name || '').toLowerCase().includes(leftSearch.toLowerCase())
  );

  const selectedMenusList = menuOptions.filter(
    m =>
      assignedSet.has(String(m.id)) &&
      (m.label || m.name || '')
        .toLowerCase()
        .includes(rightSearch.toLowerCase())
  );

  const moveRight = () => {
    if (leftSelection.length === 0) return;
    setAssignedMenuIds(prev => [...prev, ...leftSelection.map(String)]);
    setLeftSelection([]);
  };

  const moveAllRight = () => {
    const allAvailable = menuOptions
      .filter(m => !assignedSet.has(String(m.id)))
      .map(m => String(m.id));
    setAssignedMenuIds(prev => [...prev, ...allAvailable]);
    setLeftSelection([]);
  };

  const moveLeft = () => {
    if (rightSelection.length === 0) return;
    const removeIds = new Set(rightSelection.map(String));
    setAssignedMenuIds(prev => prev.filter(id => !removeIds.has(String(id))));
    setRightSelection([]);
  };

  const moveAllLeft = () => {
    setAssignedMenuIds([]);
    setRightSelection([]);
  };

  const handleSave = async () => {
    if (!hasChanges) {
      toast.info('No changes to save');
      handleClose();
      return;
    }

    setModalLoading(true);
    try {
      const currentSet = new Set(assignedMenuIds);
      const originalSet = new Set(originalMenuIds);

      const menusToAdd = assignedMenuIds
        .filter(id => !originalSet.has(String(id)))
        .map(String);

      const menusToRemove = originalAssignments.filter(
        o => !currentSet.has(String(o.menuId))
      );

      const removePromises = menusToRemove.map(mapping =>
        fetch(`${api.API_URL}/menu-role/${mapping.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(
                `Failed to remove menu ${getMenuName(menuOptions, mapping.menuId)}`
              );
            }
            return { menuId: mapping.menuId, mappingId: mapping.id };
          })
          .catch(err => {
            throw { menuId: mapping.menuId, message: err.message };
          })
      );

      const addPromises = menusToAdd.map(menuId =>
        fetch(`${api.API_URL}/menu-role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            roleId: selectedRole.id,
            menuId,
            ...DEFAULT_RIGHTS,
          }),
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(
                `Failed to assign menu ${getMenuName(menuOptions, menuId)}`
              );
            }
            return { menuId };
          })
          .catch(err => {
            throw { menuId, message: err.message };
          })
      );

      const [removeResults, addResults] = await Promise.all([
        Promise.allSettled(removePromises),
        Promise.allSettled(addPromises),
      ]);

      const enrichedRemove = removeResults.map((res, idx) => ({
        ...res,
        menuId: menusToRemove[idx]?.menuId,
      }));

      const enrichedAdd = addResults.map((res, idx) => ({
        ...res,
        menuId: menusToAdd[idx],
      }));

      const totalFailed =
        enrichedRemove.filter(r => r.status === 'rejected').length +
        enrichedAdd.filter(r => r.status === 'rejected').length;

      const message = buildAssignToastMessage(
        menuOptions,
        enrichedAdd,
        enrichedRemove
      );

      if (totalFailed === 0) {
        toast.success(message);
        handleClose();
      } else {
        toast.warning(message, { autoClose: 6000 });

        const successRemoves = enrichedRemove
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value.mappingId);

        const remainingAssignments = originalAssignments.filter(
          o => !successRemoves.includes(o.id)
        );

        const successAdds = enrichedAdd
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value.menuId);

        const newAssignedIds = [
          ...remainingAssignments.map(o => o.menuId),
          ...successAdds,
        ];

        setOriginalAssignments(
          originalAssignments.filter(o => newAssignedIds.includes(o.menuId))
        );
        setOriginalMenuIds(newAssignedIds.map(String));
        setAssignedMenuIds(newAssignedIds.map(String));
        setHasChanges(false);
      }
    } catch (error) {
      toast.error(`Save failed: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size='xl'
      backdrop='static'
      centered
    >
      <ModalHeader toggle={handleClose} className='bg-light'>
        Assign Menus to {selectedRole?.name || selectedRole?.id}
      </ModalHeader>
      <ModalBody>
        <Row className='g-3'>
          <Col md={12}>
            <Label>Menu Assignment</Label>
            <Row className='align-items-center'>
              <Col xs={12}>
                <div className='d-flex align-items-center flex-wrap gap-2 text-muted fs-12 mb-1'>
                  <i className='ri-information-line text-info fs-14'></i>
                  <span>Click</span> <span className='fw-medium'>select</span>
                  <span className='mx-1'>•</span>
                  <Badge color='light' className='text-dark border fs-10'>
                    Ctrl / ⌘
                  </Badge>
                  <span>toggle</span>
                  <span className='mx-1'>•</span>
                  <Badge color='light' className='text-dark border fs-10'>
                    Shift
                  </Badge>
                  <span>range select</span>
                </div>
              </Col>
              <Col xs={12} md={5} className='order-1 order-md-1'>
                <DualListPanel
                  title='Available Menus'
                  items={availableMenus}
                  selectedIds={leftSelection}
                  searchValue={leftSearch}
                  onSearchChange={setLeftSearch}
                  onSelectionChange={setLeftSelection}
                  lastIndex={lastLeftIndex}
                  onLastIndexChange={setLastLeftIndex}
                  emptyMessage='No available menus'
                />
              </Col>
              <Col
                xs={12}
                md={2}
                className='order-2 order-md-2 d-flex flex-row flex-md-column justify-content-center align-items-center gap-2 my-2 my-md-0'
              >
                <Button
                  color='primary'
                  size='sm'
                  onClick={moveAllRight}
                  disabled={
                    menuOptions.filter(m => !assignedSet.has(String(m.id)))
                      .length === 0
                  }
                  title='Move All'
                >
                  <i className='ri-arrow-right-double-line fs-16'></i>
                </Button>
                <Button
                  color='primary'
                  size='sm'
                  onClick={moveRight}
                  disabled={leftSelection.length === 0}
                  title='Move Selected'
                >
                  <i className='ri-arrow-right-s-line fs-16'></i>
                </Button>
                <Button
                  color='primary'
                  size='sm'
                  onClick={moveLeft}
                  disabled={rightSelection.length === 0}
                  title='Move Selected'
                >
                  <i className='ri-arrow-left-s-line fs-16'></i>
                </Button>
                <Button
                  color='primary'
                  size='sm'
                  onClick={moveAllLeft}
                  disabled={assignedMenuIds.length === 0}
                  title='Remove All'
                >
                  <i className='ri-arrow-left-double-line fs-16'></i>
                </Button>
              </Col>
              <Col xs={12} md={5} className='order-3 order-md-3'>
                <DualListPanel
                  title='Selected Menus'
                  titleClassName='border rounded p-2 bg-primary text-white text-center mb-2 fw-medium fs-12'
                  items={selectedMenusList}
                  selectedIds={rightSelection}
                  searchValue={rightSearch}
                  onSearchChange={setRightSearch}
                  onSelectionChange={setRightSelection}
                  lastIndex={lastRightIndex}
                  onLastIndexChange={setLastRightIndex}
                  emptyMessage='No selected menus'
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color='light' onClick={handleClose} disabled={modalLoading}>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSave}
          disabled={modalLoading || !hasChanges}
        >
          {modalLoading ? (
            <Spinner size='sm' className='me-2' />
          ) : (
            <i className='ri-save-line me-1'></i>
          )}
          Save Assignments
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignMenuModal;
